export type FontDisplayMethod = 'auto' | 'block' | 'swap' | 'fallback' | 'optional';

export interface FontRegistryTypefaceSource {
  path: string;
  weight?: string;
  display?: FontDisplayMethod;
  preload?: boolean;
}

export interface FontRegistryTypeface {
  name: string;
  src: FontRegistryTypefaceSource[];
  variable: string;
}

export interface IFontRegistry {
  fonts: FontRegistryTypeface[];
  seal: () => void;
}

let sealed = false;

export const FontRegistry: IFontRegistry = {
  fonts: [],
  seal: () => {
    sealed = true;
  },
};

const fontMimeTypes = {
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
  eot: 'application/vnd.ms-fontobject',
  svg: 'image/svg+xml',
};

// NextJS _document.tsx helpers
export const getFontMimeType = (filePath: string) => {
  let mimeType: string = '';

  Object.keys(fontMimeTypes).forEach((ext) => {
    if (filePath.endsWith(ext)) {
      mimeType = fontMimeTypes[ext];
    }
  });

  return mimeType;
};

export const getFontFileExtension = (filePath: string) => {
  let extension: string = '';

  Object.keys(fontMimeTypes).forEach((ext) => {
    if (filePath.endsWith(ext)) {
      extension = ext;
    }
  });

  return extension;
};

export const generateFontFaceCSS = () => {
  return FontRegistry.fonts
    .map((font) =>
      font.src
        .map(
          (file) => `\n@font-face {
  font-family: '${font.name}';
  src: url('${file.path}') format('${getFontFileExtension(file.path)}');
  font-display: ${file.display ?? 'swap'};
  ${file.weight !== undefined ? `font-weight: ${file.weight};` : ''}
}\n`,
        )
        .join(''),
    )
    .join('');
};

export const generateFontVariableCSS = () => {
  return `\n:root {${FontRegistry.fonts.map((font) => `\n  ${font.variable}: '${font.name}';\n`).join('')}}\n`;
};

// Developer-facing functions
export const registerFont = (font: FontRegistryTypeface) => {
  if (sealed) return;
  FontRegistry.fonts.push(font);
};

// Storybook managerHead helper
export function getManagerHeadFontHtml(): string {
  const fonts = FontRegistry.fonts;
  const preloadLinks = fonts
    .flatMap((font) =>
      font.src
        .filter((file) => file.preload)
        .map(
          (file) =>
            `<link rel="preload" href="${file.path}" as="font" type="${getFontMimeType(file.path)}" crossorigin="anonymous">`,
        ),
    )
    .join('\n');
  const styleContent = generateFontFaceCSS() + generateFontVariableCSS();
  return `${preloadLinks}\n<style data-font-registry>${styleContent}</style>`;
}
